import {
  type ApiError,
  type CancelBatchExecutionsResult,
  type ModuleExecutionRef,
  leftoverExecutions,
  useBatches,
  useCancelBatchExecutions,
  useModules,
} from '@ctcells/api'
import { dataSourceFor } from '@ctcells/core'
import { errorMessage, moduleTypeLabel, t } from '@ctcells/i18n'
import { useCallback, useMemo, useSyncExternalStore } from 'react'

import { ACTIVE_BATCH_LIMIT } from './activeBatchSource.ts'

/**
 * **「중지」 뒤 모듈 실행 취소** — 작업 줄 · 작업 상세가 같이 쓰는 한 벌(eric 2026-09-28).
 *
 * 🔴 **왜 있나.** `POST /batches/{id}/stop` · `recover(discard)` 는 작업 상태만 `aborted` 로 바꾸고
 *    모듈에 신호를 안 보낸다(`ctcells-core` `services/batches.stop`). 모듈은 받은 블록을 끝까지 돈다.
 *    ⇒ 중지가 **받아들여진 뒤** 그 작업의 실행을 쥔 모듈 전부에 실행 취소 중계를 이어 보낸다
 *    (`@ctcells/api` `cancelBatchExecutions`). 다른 작업의 실행은 건드리지 않는다.
 * 🔴 **낙관적 갱신이 없다.** 작업 줄은 서버 상태를 따른다 — 취소가 받아들여져도 「멈췄다」고 적지 않는다.
 *    멈춤은 모듈 스냅샷(`current_execution: null`)이 말한다.
 * 🔴 **취소가 실패해도 작업을 되돌리지 않는다** — 중지는 이미 서버에서 종착이다. 대신 작업 줄 오류 자리에
 *    「장비가 아직 돌고 있습니다 — <모듈> <사유>(<코드>)」를 **사람이 닫을 때까지** 세우고(DP-02)
 *    [다시 시도]를 준다.
 * 🔴 **E-STOP 은 이 길을 타지 않는다.** 그쪽은 서버 안전 경로다.
 *
 * ## 왜 보관소가 셸 계층에 있나
 *
 * 중지는 작업 줄에서도, 작업 상세에서도 나간다. 실패 문구는 **작업 줄**(라우트와 무관하게 늘 떠 있다)에
 * 서야 한다 — 상세를 닫아도 「장비가 아직 돈다」는 사실은 사라지지 않는다. 그래서 결과를 화면 상태가
 * 아니라 이 작은 보관소에 둔다. 서버 값이 아니라 «사람이 한 행동의 결과»라 쿼리 캐시에 두지 않는다.
 * ⚠ 새로 고치면 사라진다 — 그때는 「남은 실행」 알림(`useLeftoverExecutions`)이 스냅샷으로 다시 선다.
 */

interface CancelFailureView {
  batchId: string
  /** 다시 시도할 때 모듈에 다시 싣는 사유 */
  reason: string | undefined
  text: string
}

interface ExecutionCancelState {
  pending: boolean
  failure: CancelFailureView | null
  /** 중계가 받아들인 실행 id — 모듈이 아직 쥐고 있으면 「멈추기를 기다린다」로 적는다 */
  sent: ReadonlySet<string>
  /** 사람이 닫은 「남은 실행」 알림의 실행 id — 같은 실행으로는 다시 안 띄운다 */
  dismissed: ReadonlySet<string>
}

const INITIAL: ExecutionCancelState = {
  pending: false,
  failure: null,
  sent: new Set(),
  dismissed: new Set(),
}

let state: ExecutionCancelState = INITIAL
const listeners = new Set<() => void>()

function publish(next: Partial<ExecutionCancelState>): void {
  state = { ...state, ...next }
  for (const listener of listeners) listener()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function snapshot(): ExecutionCancelState {
  return state
}

/** 시험 전용 — 보관소를 비운다 */
export function resetExecutionCancel(): void {
  state = INITIAL
  for (const listener of listeners) listener()
}

/** 사람이 읽는 모듈 이름 — 모르는 종류면 id 를 그대로 적는다(지어내지 않는다) */
export function moduleName(ref: ModuleExecutionRef): string {
  return moduleTypeLabel(ref.moduleType) ?? ref.moduleId
}

function failureItem(module: string, error: ApiError): string {
  const reason = errorMessage(error)
  const code = error.error_code ?? (error.status > 0 ? `HTTP ${String(error.status)}` : undefined)
  return code === undefined
    ? t('strip.execCancel.item', { module, reason })
    : t('strip.execCancel.itemCode', { module, reason, code })
}

/** 결과 → 오류 문구. 실패가 없으면 `null` */
export function cancelFailureText(result: CancelBatchExecutionsResult): string | null {
  if (result.failures.length === 0) return null
  const items = result.failures.map((f) => failureItem(moduleName(f.ref), f.error)).join(' · ')
  return t('strip.execCancel.failed', { items })
}

export interface CancelAfterStop {
  /** 중지(또는 폐기)가 **받아들여진 뒤** 부른다 */
  cancelAfter: (batchId: string, reason?: string) => Promise<void>
  /** 마지막 실패를 같은 작업·사유로 다시 보낸다 */
  retry: () => void
  dismiss: () => void
  pending: boolean
  /** 작업 줄 오류 자리에 설 문구. 없으면 `null` */
  failureText: string | null
}

export function useCancelAfterStop(): CancelAfterStop {
  const current = useSyncExternalStore(subscribe, snapshot, snapshot)
  const command = useCancelBatchExecutions()
  const { mutateAsync } = command

  const cancelAfter = useCallback(
    async (batchId: string, reason?: string) => {
      if (state.pending) return
      publish({ pending: true })
      try {
        /*
         * 🔴 `mutateAsync` 다 — 약속은 부른 화면이 사라져도(상세를 닫아도) 끝까지 풀린다.
         *    `mutate(vars, {onSuccess})` 의 콜백은 언마운트되면 안 불린다.
         */
        const result = await mutateAsync({
          batchId,
          ...(reason === undefined || reason.trim() === '' ? {} : { reason }),
        })
        const text = cancelFailureText(result)
        const sent = new Set(state.sent)
        for (const ref of result.accepted) sent.add(ref.executionId)
        publish({ sent, failure: text === null ? null : { batchId, reason, text } })
      } catch (error) {
        publish({
          failure: {
            batchId,
            reason,
            text: t('strip.execCancel.lookupFailed', {
              reason: errorMessage(error as ApiError),
            }),
          },
        })
      } finally {
        publish({ pending: false })
      }
    },
    [mutateAsync],
  )

  const retry = useCallback(() => {
    const failure = state.failure
    if (failure === null) return
    void cancelAfter(failure.batchId, failure.reason)
  }, [cancelAfter])

  const dismiss = useCallback(() => {
    publish({ failure: null })
  }, [])

  return {
    cancelAfter,
    retry,
    dismiss,
    pending: current.pending,
    failureText: current.failure?.text ?? null,
  }
}

export interface LeftoverExecutions {
  /** 끝난 작업을 가리키는 실행 — 사람이 아직 안 닫은 것 */
  refs: readonly ModuleExecutionRef[]
  /** 작업 줄에 설 문구. 없으면 `null` */
  text: string | null
  /** 남은 실행이 가리키는 작업들 — [실행 취소]가 이 작업들에 취소를 보낸다 */
  batchIds: readonly string[]
  dismiss: () => void
}

/**
 * **화면을 다시 열었을 때 — 끝난 작업의 실행이 장비에 남아 있나.**
 *
 * 재료는 둘 다 서버 값이다: `GET /modules` 의 `current_execution.batch_id` 와 `GET /batches?phase=active`
 * (종착을 서버가 뺀 목록). 실행이 가리키는 작업이 그 목록에 없으면 남은 것이다.
 * 🔴 **두 조회가 «성공»했을 때만** 가른다 — 작업 목록이 아직 안 왔는데 판정하면 모든 실행이 남은 것이 된다.
 * 🔴 **자동으로 취소를 쏘지 않는다** — 알림과 [실행 취소]만 준다. 사람이 누른다.
 * ⚠ 새 조회가 아니다 — 작업 줄 · 모니터링이 이미 부르는 두 목록과 같은 캐시다(DP-01).
 */
export function useLeftoverExecutions(): LeftoverExecutions {
  const current = useSyncExternalStore(subscribe, snapshot, snapshot)
  const live = dataSourceFor('modules') === 'live' && dataSourceFor('batches') === 'live'
  const modules = useModules({ skip: !live })
  const batches = useBatches({ limit: ACTIVE_BATCH_LIMIT, phase: 'active', skip: !live })

  const all = useMemo(() => {
    if (!live || !modules.isSuccess || !batches.isSuccess) return []
    const active = new Set(batches.data.items.map((b) => b.batch_id))
    return leftoverExecutions(modules.data.items, active)
  }, [live, modules.isSuccess, modules.data, batches.isSuccess, batches.data])

  const refs = all.filter((ref) => !current.dismissed.has(ref.executionId))
  const waiting = refs.length > 0 && refs.every((ref) => current.sent.has(ref.executionId))
  const names = [...new Set(refs.map(moduleName))].join(' · ')

  const dismiss = useCallback(() => {
    const dismissed = new Set(state.dismissed)
    for (const ref of all) dismissed.add(ref.executionId)
    publish({ dismissed })
  }, [all])

  return {
    refs,
    text:
      refs.length === 0
        ? null
        : t(waiting ? 'strip.execCancel.waiting' : 'strip.execCancel.leftover', { modules: names }),
    batchIds: [...new Set(refs.map((ref) => ref.batchId))],
    dismiss,
  }
}
